/**
 * Relative-time labels shared across screens. Each takes an optional `now` so the
 * output is deterministic under test.
 */

/** Coarse "time ago" for last-seen style columns: "Just now", "5 min ago", "3h ago", "2 days ago". */
export function timeAgo(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'Never';
  const min = Math.floor((now.getTime() - then) / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

/** Calendar-day label: "Today", "Yesterday", otherwise e.g. "Monday, September 21". */
export function dayLabel(d: Date, now: Date = new Date()): string {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

/** Clock time in the design's lower-case form, e.g. "10:14 am". */
export function clockTime(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h % 12 || 12}:${m} ${h < 12 ? 'am' : 'pm'}`;
}

/**
 * Timestamp for a comment in a thread: "just now", "Today, 10:14 am", "Yesterday",
 * then the calendar day (with the year once it is not the current one).
 */
export function commentTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // A timestamp slightly in the future (client clock behind the server) is "just now" too.
  if (now.getTime() - d.getTime() < 60000) return 'just now';
  const day = dayLabel(d, now);
  if (day === 'Today') return `Today, ${clockTime(d)}`;
  if (day === 'Yesterday') return day;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}
